import React from 'react';
import { Lock, ShoppingCart, BookOpen } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { comics } from '../../data/comics';
import { EncryptedImage } from '../EncryptedImage';
import { FEATURED_IDS } from '../../data/monthly';

const SPARKLES = [
  { left: '6%', delay: '0s', duration: '3.4s' },
  { left: '22%', delay: '1.1s', duration: '4.1s' },
  { left: '78%', delay: '0.5s', duration: '3.7s' },
  { left: '92%', delay: '1.7s', duration: '4.4s' },
  { left: '50%', delay: '2.3s', duration: '3.9s' },
];

// A comic-book starburst (the "NEW!" sticker): a polygon with alternating long and
// short points.
const BURST = (() => {
  const points = [];
  const spikes = 14;
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? 50 : 38;
    const a = (Math.PI * i) / spikes;
    points.push(`${(50 + r * Math.sin(a)).toFixed(1)}% ${(50 - r * Math.cos(a)).toFixed(1)}%`);
  }
  return `polygon(${points.join(', ')})`;
})();

function FeaturedCard({ comic, isLocked, isUnlocking, showUnlockInput, language, t, onComicClick, onUnlockClick, onBuyClick, unlockedComics }) {
  const isUnlocked = unlockedComics.includes(comic.id);

  return (
    <div className="fp-panel fp-panel--gold" style={styles.card}>
      <div className="fp-halftone" />

      {/* the "NEW!" sticker */}
      <div className="fp-wobble" style={styles.burstWrap}>
        <div style={styles.burstShadow} />
        <div style={styles.burst}>{language === 'es' ? '¡NUEVO!' : 'NEW!'}</div>
      </div>

      <div style={styles.stage}>
        <div className="fp-spin-slow" style={styles.rays} />

        {SPARKLES.map((spark, i) => (
          <span
            key={i}
            style={{
              ...styles.spark,
              left: spark.left,
              animation: `fpSpark ${spark.duration} ease-out infinite`,
              animationDelay: spark.delay,
            }}
          >✦</span>
        ))}

        <div className="fp-float" style={styles.coverFloat}>
          <div
            className="fp-cover"
            style={{
              ...styles.coverContainer,
              cursor: isLocked ? 'default' : 'pointer',
              ...(isUnlocking ? styles.unlockingCard : {}),
            }}
            onClick={() => onComicClick(comic.id)}
          >
            <EncryptedImage
              src={language === 'es' && comic.coverUrlEs ? comic.coverUrlEs : comic.coverUrl}
              alt={comic.title}
              style={styles.comicCover}
              priority={true}
            />

            {isLocked && !isUnlocking && (
              <span style={styles.lockBadge}><Lock size={15} color="#14061f" strokeWidth={3} /></span>
            )}

            {isUnlocking && (
              <div style={styles.unlockAnimationOverlay}>
                <div style={styles.unlockFlash}></div>
                <div style={styles.unlockSparkles}>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        ...styles.unlockSparkle,
                        animationDelay: `${i * 0.1}s`,
                        left: `${10 + (i % 4) * 25}%`,
                        top: `${10 + Math.floor(i / 4) * 30}%`,
                      }}
                    >✨</div>
                  ))}
                </div>
                <div style={styles.unlockText}>{t('unlocked')}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <h3 className="fp-title fp-title--white" style={styles.comicTitle}>{comic.title}</h3>

      <div style={styles.actions}>
        {isLocked && showUnlockInput !== comic.id && !isUnlocking && (
          <button
            className="fp-btn fp-btn--lg fp-btn--block fp-btn--shine"
            onClick={(e) => onUnlockClick(comic.id, e)}
            data-testid={`featured-unlock-btn-${comic.id}`}
          >
            <Lock size={19} strokeWidth={3} />
            {t('unlock')}
          </button>
        )}

        {isUnlocked ? (
          <button
            className="fp-btn fp-btn--gold fp-btn--lg fp-btn--block fp-btn--shine"
            onClick={() => onComicClick(comic.id)}
            data-testid={`featured-read-btn-${comic.id}`}
          >
            <BookOpen size={19} strokeWidth={3} />
            {language === 'es' ? 'Leer ahora' : 'Read now'}
          </button>
        ) : (
          <button
            className="fp-btn fp-btn--green fp-btn--block"
            onClick={(e) => {
              if (!unlockedComics.includes(comic.id) && comic.id !== 2) {
                if (comic.buyUrl) {
                  window.open(comic.buyUrl, '_blank');
                } else {
                  onBuyClick(comic.id, e);
                }
              }
            }}
            disabled={unlockedComics.includes(comic.id)}
            data-testid={`featured-buy-btn-${comic.id}`}
          >
            <ShoppingCart size={17} strokeWidth={3} />
            {t('buy')}
          </button>
        )}
      </div>
    </div>
  );
}

export const FeaturedSection = ({
  unlockedComics,
  unlockingComic,
  showUnlockInput,
  onComicClick,
  onUnlockClick,
  onBuyClick,
}) => {
  const { t, language } = useLanguage();
  const featuredComics = FEATURED_IDS.map(id => comics.find(c => c.id === id)).filter(Boolean);

  if (featuredComics.length === 0) return null;

  return (
    <section style={styles.featuredSection}>
      <div className="fp-rise" style={styles.head}>
        <h2 className="fp-ribbon fp-ribbon--pink" style={styles.ribbon}>{t('monthlyFeatured')}</h2>
        <p style={styles.featuredDescription}>{t('featuredDescription')}</p>
      </div>

      <div style={styles.featuredCardsContainer}>
        {featuredComics.map((comic, i) => (
          <div key={comic.id} className="fp-rise" style={{ width: '100%', '--i': i + 1 }}>
            <FeaturedCard
              comic={comic}
              isLocked={comic.locked && !unlockedComics.includes(comic.id)}
              isUnlocking={unlockingComic === comic.id}
              showUnlockInput={showUnlockInput}
              language={language}
              t={t}
              onComicClick={onComicClick}
              onUnlockClick={onUnlockClick}
              onBuyClick={onBuyClick}
              unlockedComics={unlockedComics}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

const styles = {
  featuredSection: {
    padding: '22px 14px 10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  head: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '18px',
  },
  ribbon: {
    margin: 0,
    fontSize: '21px',
    textAlign: 'center',
  },
  featuredDescription: {
    fontSize: '13.5px',
    fontWeight: 800,
    color: '#fff',
    textAlign: 'center',
    margin: 0,
    textShadow: '0 2px 0 rgba(0,0,0,0.45)',
  },
  featuredCardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '26px',
    width: '100%',
    maxWidth: '420px',
  },
  card: {
    width: '100%',
    padding: '18px 16px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  burstWrap: {
    position: 'absolute',
    top: '-16px',
    left: '-8px',
    width: '86px',
    height: '86px',
    zIndex: 6,
  },
  burstShadow: {
    position: 'absolute',
    inset: '-4px',
    background: '#000',
    clipPath: BURST,
  },
  burst: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #fff27a 0%, #ffd400 55%, #ff9d00 100%)',
    clipPath: BURST,
    color: '#d4127a',
    fontSize: '15px',
    fontWeight: 900,
    fontStyle: 'italic',
    letterSpacing: '0.3px',
  },
  stage: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '6px 0 14px',
  },
  rays: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '520px',
    height: '520px',
    margin: '-260px 0 0 -260px',
    background: 'repeating-conic-gradient(from 0deg, rgba(255,212,0,0.38) 0deg 9deg, rgba(255,212,0,0) 9deg 18deg)',
    WebkitMaskImage: 'radial-gradient(circle, #000 22%, transparent 62%)',
    maskImage: 'radial-gradient(circle, #000 22%, transparent 62%)',
    pointerEvents: 'none',
    willChange: 'transform',
  },
  spark: {
    position: 'absolute',
    bottom: '18px',
    fontSize: '15px',
    color: '#fff6a8',
    opacity: 0,
    textShadow: '0 0 6px rgba(255, 215, 0, 0.9)',
    pointerEvents: 'none',
    zIndex: 3,
  },
  coverFloat: {
    position: 'relative',
    width: '66%',
    maxWidth: '250px',
    zIndex: 2,
  },
  coverContainer: {
    width: '100%',
    borderRadius: '16px',
    borderWidth: '4px',
    boxShadow: '0 0 0 3px #ffd400, 0 0 0 6px #000, 0 12px 0 rgba(0,0,0,0.55), 0 18px 28px rgba(0,0,0,0.5)',
  },
  comicCover: {
    width: '100%',
    aspectRatio: '2/3',
    objectFit: 'cover',
    display: 'block',
  },
  lockBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: '#ffd400',
    border: '2.5px solid #000',
    boxShadow: '0 3px 0 #000',
    zIndex: 2,
  },
  unlockingCard: {
    animation: 'unlockGlow 1.5s ease-in-out',
    zIndex: 10,
  },
  comicTitle: {
    position: 'relative',
    margin: '6px 0 14px',
    fontSize: '23px',
    textTransform: 'none',
    lineHeight: 1.1,
  },
  actions: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '13px',
  },
  unlockAnimationOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  unlockFlash: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.9) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(255, 20, 147, 0.9) 100%)',
    animation: 'flashUnlock 1.5s ease-out forwards',
  },
  unlockSparkles: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  unlockSparkle: {
    position: 'absolute',
    fontSize: '24px',
    animation: 'sparkleUnlock 1s ease-out forwards',
    opacity: 0,
  },
  unlockText: {
    position: 'relative',
    fontSize: '26px',
    fontWeight: 900,
    fontStyle: 'italic',
    color: '#FFD700',
    textShadow: 'var(--fp-outline-lg)',
    animation: 'textPopIn 0.5s ease-out 0.3s both',
    letterSpacing: '1px',
  },
};

export default FeaturedSection;
